import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Globe, Trophy, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

// (Msg 754) Hero static labels translated via useLanguage when ES is on.
const HERO_T = {
  en: {
    tagline: 'The Ultimate Soccer Event',
    fanGuide: 'Fan Guide',
    dateBadge: 'June 11 - July 19, 2026',
    nations: '3 Nations',
    matches: '103 Matches',
    teams: '48 Teams',
    ctaNewsletter: 'Get Soccer Updates',
    ctaVenue: 'Power Up Your Venue',
    upcomingEvents: 'Upcoming Events',
  },
  es: {
    tagline: 'El Evento de Fútbol Definitivo',
    fanGuide: 'Guía de Fans',
    dateBadge: '11 de Junio - 19 de Julio, 2026',
    nations: '3 Naciones',
    matches: '104 Partidos',
    teams: '48 Equipos',
    ctaNewsletter: 'Recibe Noticias',
    ctaVenue: 'Promociona Tu Bar',
    upcomingEvents: 'Próximos Eventos',
  },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FLAGSHIP_EVENTS = [
  { event_name: 'World Soccer Kickoff Festival & Party', event_description: 'Join us for the ultimate kickoff celebration — beer gardens, food trucks, live music, and VIP takeovers at TMU Toronto.', venue_name: 'TMU Toronto', city: 'Toronto', country: 'Canada', event_date: 'June 11-14, 2026' },
  { event_name: 'World Soccer Finale Festival & Party', event_description: 'The grand finale — celebrate the championship with fans from around the world at Toronto\'s biggest soccer party.', venue_name: 'TMU Toronto', city: 'Toronto', country: 'Canada', event_date: 'July 18-19, 2026' },
];

export const Hero = () => {
  const { lang } = useLanguage();
  const L = HERO_T[lang] || HERO_T.en;
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [userCity, setUserCity] = useState('Local');

  useEffect(() => {
    fetchUpcomingEvents();
    fetch('https://ipapi.co/json/').then(r => r.json()).then(d => {
      if (d && d.city) setUserCity(d.city);
    }).catch(() => {});
  }, []);

  const fetchUpcomingEvents = async () => {
    try {
      const response = await axios.get(`${API}/venues/listings?status=published`);
      const data = Array.isArray(response.data) ? response.data : (response.data?.items || []);
      const sorted = data
        .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
        .slice(0, 5);
      setUpcomingEvents(sorted.length > 0 ? sorted : FLAGSHIP_EVENTS);
    } catch (error) {
      setUpcomingEvents(FLAGSHIP_EVENTS);
    }
  };

  useEffect(() => {
    if (upcomingEvents.length > 0) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % upcomingEvents.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [upcomingEvents]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % upcomingEvents.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + upcomingEvents.length) % upcomingEvents.length);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-28">
      {/* Background — Mobile: looping video; Desktop: high-res still image */}
      <div className="absolute inset-0">
        {/* Mobile: cinematic stadium video (Canva-generated, MP4 H.264) */}
        <video
          className="md:hidden w-full h-full object-cover"
          src={`${BACKEND_URL}/api/static/hero/hero.mp4`}
          poster="https://images.unsplash.com/photo-1556816213-431b32a38191?w=800&q=70"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          data-testid="hero-bg-video-mobile"
        />
        {/* Desktop / tablet: user-provided hero — Jannes Glas (free Unsplash License) goal-net kick */}
        <img
          src="https://customer-assets.emergentagent.com/job_3370f098-5fd3-4196-b089-ed50920243f0/artifacts/ztd0xfz0_jannes-glas-cuhQcfp3By4-unsplash.jpg"
          alt="Soccer player kicking a ball into the goal net at night"
          className="hidden md:block w-full h-full object-cover object-center"
          data-testid="hero-bg-image-desktop"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0E2E53]/55 via-[#FF6B35]/35 to-[#FF006E]/50"></div>
      </div>

      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 border-4 border-white rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 border-4 border-white rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/3 right-1/4 w-24 h-24 border-4 border-white rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="font-['Bebas_Neue'] text-5xl sm:text-6xl md:text-7xl tracking-tighter uppercase text-white mb-6 drop-shadow-lg">
            <span className="block text-white text-6xl sm:text-7xl md:text-8xl mb-2 drop-shadow-2xl">2026</span>
            World Soccer Party
          </h1>
          
          <p className="text-xl sm:text-2xl max-w-3xl mx-auto mb-8 leading-relaxed font-semibold drop-shadow-md">
            <span className="text-[#FFD700]">{L.tagline}</span> <span className="text-[#FFD700]">{userCity} {L.fanGuide}</span>
          </p>

          <div className="flex flex-wrap items-center justify-center gap-6 mb-12">
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-3 rounded-lg">
              <Calendar size={24} weight="bold" className="text-white" />
              <span className="font-bold text-white">{L.dateBadge}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-3 rounded-lg">
              <Globe size={24} weight="bold" className="text-white" />
              <span className="font-bold text-white">{L.nations}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-3 rounded-lg">
              <Trophy size={24} weight="bold" className="text-white" />
              <span className="font-bold text-white">{L.matches}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-3 rounded-lg">
              <Trophy size={24} weight="bold" className="text-white" />
              <span className="font-bold text-white">{L.teams}</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <a 
              href="#newsletter" 
              className="bg-white text-[#FF6B35] px-8 py-4 font-bold uppercase tracking-wider hover:bg-gray-100 transition-all inline-block rounded-lg shadow-lg"
              data-testid="hero-cta-newsletter"
            >
              {L.ctaNewsletter}
            </a>
            <Link 
              to="/partner-venues" 
              className="border-2 border-white text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-white hover:text-[#FF6B35] transition-all inline-block rounded-lg"
              data-testid="hero-cta-partner-venues"
            >
              {L.ctaVenue}
            </Link>
          </div>

          {/* Upcoming Events Carousel */}
          {upcomingEvents.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="max-w-3xl mx-auto"
            >
              <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-2xl">
                <h3 className="font-['Bebas_Neue'] text-2xl uppercase text-[#0E2E53] mb-4">{L.upcomingEvents}</h3>
                
                <div className="relative">
                  <div className="overflow-hidden">
                    <motion.div
                      key={currentSlide}
                      initial={{ opacity: 0, x: 100 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ duration: 0.5 }}
                      className="text-left"
                    >
                      <h4 className="font-bold text-xl text-gray-900 mb-2">{upcomingEvents[currentSlide]?.event_name}</h4>
                      <p className="text-gray-600 mb-3">{upcomingEvents[currentSlide]?.event_description}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-700">
                        <span className="font-semibold">{upcomingEvents[currentSlide]?.venue_name}</span>
                        <span>•</span>
                        <span>{upcomingEvents[currentSlide]?.city}, {upcomingEvents[currentSlide]?.country}</span>
                        <span>•</span>
                        <span>{upcomingEvents[currentSlide]?.event_date}</span>
                      </div>
                    </motion.div>
                  </div>

                  {/* Carousel Controls */}
                  <div className="flex items-center justify-between mt-4">
                    <button
                      onClick={prevSlide}
                      className="p-2 bg-[#0E2E53] text-white rounded-lg hover:bg-[#1a3a5c] transition-colors"
                      data-testid="carousel-prev"
                    >
                      <CaretLeft size={20} weight="bold" />
                    </button>
                    <div className="flex gap-2">
                      {upcomingEvents.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentSlide(index)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            index === currentSlide ? 'bg-[#FF6B35] w-8' : 'bg-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <button
                      onClick={nextSlide}
                      className="p-2 bg-[#0E2E53] text-white rounded-lg hover:bg-[#1a3a5c] transition-colors"
                      data-testid="carousel-next"
                    >
                      <CaretRight size={20} weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white rounded-full"></div>
        </div>
      </motion.div>
    </section>
  );
};
